import type { APIEntityType, APIFieldGlobalAttributes } from '@ardoq/api-types';
import {
  filterTypeByApiFieldType,
  QueryBuilderFilterType,
} from '@ardoq/query-builder';
import { dateRangeOperations } from '@ardoq/date-range';
import { COMPONENT_BUILT_IN_FIELDS, REFERENCE_BUILT_IN_FIELDS } from '../utils';
import { translateFormattingRules } from './translateRule';
import { FormattingRule } from '@ardoq/data-model';

/**
 * Determines the filter type for a given field.
 */
const getFilterTypeForField = (
  field: string,
  entityType: APIEntityType.COMPONENT | APIEntityType.REFERENCE,
  customFields: APIFieldGlobalAttributes[]
): QueryBuilderFilterType | undefined => {
  // Check built-in fields first
  const builtInFields =
    entityType === 'component'
      ? COMPONENT_BUILT_IN_FIELDS
      : REFERENCE_BUILT_IN_FIELDS;

  if (builtInFields[field]) {
    return builtInFields[field];
  }

  // it's handle both DATE_RANGE and DATE_TIME_RANGE
  if (dateRangeOperations.isCombinedDateRangeFieldNames(field)) {
    return QueryBuilderFilterType.DATE_RANGE;
  }

  // Check custom fields
  const customField = customFields.find(f => f.name === field);
  if (!customField) return undefined;

  // Use the mapping from query-builder (with new conditional formatting flag = true)
  const fieldTypeMapping = filterTypeByApiFieldType(true);
  return fieldTypeMapping[customField.type];
};

/**
 * Gets select options for a field if it's a SELECT type.
 */
const getSelectOptionsForField = (
  field: string,
  entityType: APIEntityType.COMPONENT | APIEntityType.REFERENCE,
  customFields: APIFieldGlobalAttributes[],
  additionalOptions?: {
    componentTypeNames?: string[];
    referenceTypeNames?: string[];
  }
): Array<{ value: string; label: string }> | undefined => {
  // Handle built-in type fields
  if (field === 'type') {
    if (entityType === 'component' && additionalOptions?.componentTypeNames) {
      return additionalOptions.componentTypeNames.map(name => ({
        value: name,
        label: name,
      }));
    }
    if (entityType === 'reference' && additionalOptions?.referenceTypeNames) {
      return additionalOptions.referenceTypeNames.map(name => ({
        value: name,
        label: name,
      }));
    }
  }

  // Check custom fields
  const customField = customFields.find(f => f.name === field);
  if (!customField || !customField.defaultValue) {
    return undefined;
  }

  // Return options from field default value
  if (typeof customField.defaultValue === 'string') {
    return customField.defaultValue.split(',').map(value => ({
      value,
      label: value,
    }));
  }
};

/**
 * Creates a metadata object with field information for migration.
 */
export const createMigrationMetadata = (options: {
  componentFields: APIFieldGlobalAttributes[];
  referenceFields: APIFieldGlobalAttributes[];
  componentTypeNames?: string[];
  referenceTypeNames?: string[];
}): {
  getFieldType: (
    field: string,
    entityType: APIEntityType.COMPONENT | APIEntityType.REFERENCE
  ) => QueryBuilderFilterType | undefined;
  getSelectOptions: (
    field: string,
    entityType: APIEntityType.COMPONENT | APIEntityType.REFERENCE
  ) => Array<{ value: string; label: string }> | undefined;
} => {
  return {
    getFieldType: (field, entityType) =>
      getFilterTypeForField(
        field,
        entityType,
        entityType === 'component'
          ? options.componentFields
          : options.referenceFields
      ),
    getSelectOptions: (field, entityType) =>
      getSelectOptionsForField(
        field,
        entityType,
        entityType === 'component'
          ? options.componentFields
          : options.referenceFields,
        {
          componentTypeNames: options.componentTypeNames,
          referenceTypeNames: options.referenceTypeNames,
        }
      ),
  };
};

export function migrateRules(params: {
  rules: FormattingRule[];
  relatedComponentFields: APIFieldGlobalAttributes[];
  relatedReferenceFields: APIFieldGlobalAttributes[];
  componentTypeNames: string[];
  referenceTypeNames: string[];
}) {
  const {
    rules,
    relatedComponentFields,
    relatedReferenceFields,
    componentTypeNames,
    referenceTypeNames,
  } = params;

  const metadata = createMigrationMetadata({
    componentFields: relatedComponentFields,
    referenceFields: relatedReferenceFields,
    componentTypeNames,
    referenceTypeNames,
  });

  return translateFormattingRules(rules, metadata);
}
